import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';

type ContextSubMenuProps = {
  className?: string;
  visible: boolean;
  children?: React.ReactNode;
};

@observer
export default class ContextSubMenu extends React.Component<ContextSubMenuProps> {
  @observable.ref private _subMenu: React.RefObject<HTMLDivElement | null> = React.createRef();
  @observable private _fixedLeft: number = 0;
  @observable private _fixedTop: number = 0;
  @observable private _useFixed: boolean = false;
  private _resizeObserver: ResizeObserver | null = null;
  @observable private _width: number = 0;
  @observable private _height: number = 0;

  constructor(props: ContextSubMenuProps) {
    super(props);
    makeObservable(this);
  }

  componentDidUpdate(prevProps: ContextSubMenuProps) {
    if (this._subMenu.current) {
      this._subMenu.current.className = this._getClassName();
    }
    // Recalculate fixed position when the submenu becomes visible
    if (this.props.visible && !prevProps.visible) {
      setTimeout(() => this._calcFixedPosition(), 0);
    }
  }

  componentDidMount() {
    if (this._subMenu.current) {
      this._subMenu.current.className = this._getClassName();

      this._resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          this._onResize(width, height);
          // Recalculate when the submenu gains real dimensions (i.e. becomes visible)
          if (width > 0 && height > 0) {
            this._calcFixedPosition();
          }
        }
      });
      this._resizeObserver.observe(this._subMenu.current);
    }
  }

  componentWillUnmount() {
    if (this._resizeObserver && this._subMenu.current) {
      this._resizeObserver.unobserve(this._subMenu.current);
      this._resizeObserver.disconnect();
    }
  }

  render() {
    const className = this._getClassName();
    // Use fixed positioning so the submenu escapes any overflow:scroll/hidden parent
    const style: React.CSSProperties = this._useFixed
      ? { position: 'fixed', left: `${this._fixedLeft}px`, top: `${this._fixedTop}px` }
      : {};
    return (
      <div ref={this._subMenu} className={className} style={style}>
        {this.props.children}
      </div>
    );
  }

  @action.bound
  private _onResize(width?: number, height?: number) {
    if (width) { this._width = width; }
    if (height) { this._height = height; }
  }

  @action.bound
  private _calcFixedPosition() {
    const subMenu = this._subMenu.current;
    if (!subMenu) return;

    const parent = subMenu.parentElement;
    if (!parent) return;

    const rect = parent.getBoundingClientRect();
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const menuW = this._width || subMenu.offsetWidth || 200;
    const menuH = this._height || subMenu.offsetHeight || 100;

    // Open to the right when there's room, otherwise to the left
    const left = rect.right + menuW < screenW - 5 ? rect.right : rect.left - menuW;
    // Align top with parent item, clamped within viewport
    const top = Math.min(rect.top, Math.max(5, screenH - menuH - 5));

    this._fixedLeft = left;
    this._fixedTop = top;
    this._useFixed = true;
  }

  private _getClassName(): string {
    return 'contextMenu children-wrap' + (this.props.className ? ` ${this.props.className}` : '');
  }
}
